import { FormikProvider, useFormik } from 'formik';
import { rgba } from 'polished';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useErrorBoundary, useFeature, useViewerRoles, useWorkspace } from '@treams/treams-hooks';
import {
	ChessQueenIcon,
	GearIcon,
	HeartRateIcon,
	LibraryIcon,
	MeetingIcon,
} from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Button,
	Callout,
	GraphQLErrorMessage,
	Heading3,
	Input,
	Label,
	Paragraph,
	Select,
	tokens,
} from '@treams/treams-ui';

import AssociatedIdentities from './AssociatedIdentities';

const Header = styled(Flex)`
	align-items: center;
	margin-bottom: ${tokens.gap.group}px;
	padding-bottom: ${tokens.gap.group}px;
	border-bottom: 1px solid ${tokens.border.subtle};
`;

const Anchor = styled(Box)`
	color: ${(props) => props.theme.colors.primary};
	border-bottom: 1px solid ${(props) => rgba(props.theme.colors.primary, 0.25)};
	cursor: pointer;

	&:hover {
		background: ${(props) => rgba(props.theme.colors.primary, 0.1)};
		border-bottom-color: transparent;

		&:active {
			background: ${(props) => rgba(props.theme.colors.primary, 0.25)};
		}
	}
`;

type AuthenticationProps = {
	create?: boolean;
	user?: graphql.User;
	disableEditing?: boolean;
	identityProviders?: graphql.IdentityProvider[];
	onChange?: (data: { username: string; email: string; externalReferenceID?: string }) => void;
};

export default function SignIn({
	create,
	user,
	disableEditing,
	identityProviders,
	onChange,
}: AuthenticationProps) {
	const { t } = useTranslation();
	const workspace = useWorkspace();
	const { loading: rolesLoading, isAdministrator, isWorkspaceSuperuser } = useViewerRoles();

	const hasHrBusinessPartnerRole = useFeature(graphql.FeatureName.HrBusinessPartnerRole);

	const canViewUsername = create || !!user?.isViewer || isAdministrator || isWorkspaceSuperuser;

	const [update] = graphql.useUpdateUserMutation();
	const [associate] = graphql.useEnsureAssociatedIdentitiesMutation();
	const [assign] = graphql.useAssignRoleMutation();
	const [revoke] = graphql.useRevokeRoleMutation();
	const [send] = graphql.useSendTemporaryPasswordMutation();

	const [editing, setEditing] = useState(create ?? false);
	const [sent, setSent] = useState(false);

	const [error, attempt, reset] = useErrorBoundary();

	const initialRole =
		user?.roles?.find((role) =>
			[
				'workspace-super-user',
				'workspace-administrator',
				'workspace-learning-officer',
				'workspace-engagement-officer',
				'workspace-hr-business-partner',
			].includes(role.role.id),
		)?.role.type ?? graphql.RoleType.Member;

	const formik = useFormik({
		initialValues: {
			username: user?.username!,
			email: user?.email!,
			associatedIdentities: user?.associatedIdentities?.map((identity) => ({
				identityProviderID: identity.identityProvider.id,
				name: identity.name,
			}))!,
			externalReferenceID: user?.externalReferenceID!,
			role: initialRole,
		},
		onSubmit: () => {},
	});

	useEffect(() => {
		formik.setValues({
			username: user?.username!,
			email: user?.email!,
			associatedIdentities: user?.associatedIdentities?.map((identity) => ({
				identityProviderID: identity.identityProvider.id,
				name: identity.name,
			}))!,
			externalReferenceID: user?.externalReferenceID!,
			role: initialRole,
		});
	}, [!!user, user?.id]);

	useEffect(() => {
		if (!user) {
			return;
		}

		onChange?.(formik.values);

		reset();
	}, [JSON.stringify(formik.values)]);

	useEffect(() => {
		if (error && !editing) {
			setEditing(true);
		}
	}, [!!error, editing]);

	const onSave = attempt(async () => {
		if (!user) {
			return;
		}

		setEditing(false);
		await update({
			variables: {
				input: {
					id: user.id,
					email: formik.values.email!,
					firstName: user.firstName,
					lastName: user.lastName,
					groupID: user.group.id,
					locale: user.locale,
					username: formik.values.username!,
					activatedAt: user.activatedAt,
					localDeactivatedAt: user.localDeactivatedAt,
					externalReferenceID: formik.values.externalReferenceID,
				},
			},
		});
		const userID = user.id;

		await Promise.all(
			identityProviders?.map((provider) => {
				const names = formik.values.associatedIdentities
					.filter((identity) => identity.identityProviderID === provider.id)
					.map((identity) => identity.name);

				return associate({
					variables: {
						input: {
							userID,
							identityProviderID: provider.id,
							names,
						},
					},
				});
			}) ?? [],
		);

		if (formik.values.role === initialRole) {
			return;
		}

		if (formik.values.role === graphql.RoleType.Member) {
			await revoke({
				variables: {
					input: {
						userID: user.id,
						roleID: user.roles.find((value) => value.role.type === initialRole)!.id,
						targetType: graphql.RoleTargetType.Workspace,
						targetID: workspace.id,
					},
				},
			});
		} else {
			await assign({
				variables: {
					input: {
						userID: user.id,
						roleID: `workspace-${formik.values.role.replaceAll('_', '-').toLowerCase()}`,
						targetType: graphql.RoleTargetType.Workspace,
						targetID: workspace.id,
						isIndirect: false,
					},
				},
			});
		}
	});

	const roleOptions = () => {
		const baseRoles = [
			{
				value: graphql.RoleType.SuperUser,
				title: t('roles.workspace-super-user'),
				icon: <ChessQueenIcon />,
			},
			{
				value: graphql.RoleType.LearningOfficer,
				title: t('roles.workspace-learning-officer'),
				icon: <LibraryIcon />,
			},
			{
				value: graphql.RoleType.EngagementOfficer,
				title: t('roles.workspace-engagement-officer'),
				icon: <HeartRateIcon />,
			},
			{
				value: graphql.RoleType.Administrator,
				title: t('roles.administrator'),
				icon: <GearIcon />,
			},
		];

		const hrRole = {
			value: graphql.RoleType.HrBusinessPartner,
			title: t('roles.HRBusinessPartner'),
			icon: <MeetingIcon />,
		};

		const tailRoles = [
			undefined,
			{
				value: graphql.RoleType.Member,
				title: t('common.none'),
			},
		];

		return hasHrBusinessPartnerRole
			? [...baseRoles, hrRole, ...tailRoles]
			: [...baseRoles, ...tailRoles];
	};

	return (
		<FormikProvider value={formik}>
			<Header>
				<Heading3 flexGrow={1} flexShrink={1} mr={2} mb={0}>
					{t('settings.employees.authentication.title')}
				</Heading3>
				{!create && (rolesLoading || canViewUsername) && (
					<Box flexGrow={0} flexShrink={0}>
						{user && !rolesLoading ? (
							editing ? (
								<Button type="primary" title={t('common.save')} onClick={onSave} />
							) : (
								<Button
									type="secondary"
									disabled={!user.viewerCanUpdate}
									title={t('common.edit')}
									onClick={() => setEditing(true)}
								/>
							)
						) : (
							<Button.Skeleton />
						)}
					</Box>
				)}
			</Header>
			{error && (
				<Box mb={2}>
					<GraphQLErrorMessage error={error} />
				</Box>
			)}
			<Flex mb={2}>
				{(rolesLoading || canViewUsername) && (
					<Box mr={2} flexGrow={1} flexShrink={1} flexBasis="30%">
						<Label mb={2}>{t('settings.employees.authentication.username')}:</Label>
						{user && !rolesLoading ? (
							<Input.Formik name="username" disabled={!editing || disableEditing} />
						) : (
							<Input.Skeleton />
						)}
					</Box>
				)}
				<Box flexGrow={1} flexShrink={1} flexBasis="70%">
					<Label mb={2}>{t('settings.employees.authentication.email')}:</Label>
					{user ? (
						<Input.Formik name="email" disabled={!editing || disableEditing} />
					) : (
						<Input.Skeleton />
					)}
					{!create && user && (
						<Flex mt={2}>
							{sent ? (
								<Paragraph>{t('settings.employees.authentication.sent')}</Paragraph>
							) : (
								<Anchor
									onClick={() => {
										setSent(true);
										send({ variables: { input: { userID: user!.id } } });
									}}
								>
									{t('settings.employees.authentication.resend')}
								</Anchor>
							)}
						</Flex>
					)}
				</Box>
			</Flex>
			<Flex mb={2}>
				{!create && (
					<Box mr={2} flexGrow={1} flexShrink={1} flexBasis="50%">
						<Label mb={2}>{t('settings.employees.authentication.role')}:</Label>
						{user ? (
							<Select.Formik
								key={`${editing}`}
								name="role"
								search={false}
								options={roleOptions()}
								disabled={!editing}
							/>
						) : (
							<Input.Skeleton />
						)}
					</Box>
				)}
				<Box flexGrow={1} flexShrink={1} flexBasis="50%">
					<Label mb={2}>{t('settings.employees.personal.hris')}:</Label>
					{user ? (
						<Input.Formik key={`${editing}`} name="externalReferenceID" disabled={!editing} />
					) : (
						<Input.Skeleton />
					)}
				</Box>
			</Flex>
			{formik.values.role !== initialRole &&
				(formik.values.role === graphql.RoleType.SuperUser ? (
					<Callout color="manager">
						{t('settings.employees.authentication.roles.warning.expansion', {
							name: user?.firstName,
						})}
					</Callout>
				) : (
					initialRole === graphql.RoleType.SuperUser && (
						<Callout color="manager">
							{t('settings.employees.authentication.roles.warning.reduction', {
								name: user?.firstName,
							})}
						</Callout>
					)
				))}
			{user &&
				(identityProviders ?? []).length > 0 &&
				((formik.values.associatedIdentities ?? []).length > 0 || editing) && (
					<AssociatedIdentities
						readOnly={!editing}
						email={formik.values.email ?? ''}
						username={formik.values.username ?? ''}
						user={user}
						associatedIdentities={formik.values.associatedIdentities ?? []}
						setAssociatedIdentities={(identities) => {
							formik.setFieldValue('associatedIdentities', identities);
						}}
					/>
				)}
		</FormikProvider>
	);
}
